'use client';

import { FC, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { useProducts } from 'stores/product';
import TabItem from 'components/common/TabItem';
import WhereToBuyBtn from 'components/button/where-to-buy-btn';
import { useDeviceDetect } from '@/utils/useDeviceDetect';
import { useStore } from 'zustand';
import { useMapStore } from 'stores/map/mapStore';
import { ThemeBase, usePageTheme } from '@/stores/page-theme';
import { twMerge } from 'tailwind-merge';
import 'react-loading-skeleton/dist/skeleton.css';
import Heading from '@/components/common/Heading';
import { navigationService } from '@/services/navigation';
import { BUTTON_TEXT } from '@/utils/constants';
import IconComponent from '@/components/FlashCards/Icon';

export interface NavItem {
  path: string;
  label: string;
}

export const SubNavigation: FC<{
  slug: string;
  [key: string]: any;
}> = ({ slug }) => {
  const { atTop } = navigationService();
  const isMobile = useDeviceDetect();
  const { bgColor, themeBase } = useStore(usePageTheme, (state) => ({
    themeBase: state.themeBase,
    bgColor: state.bgColor,
  }));
  const { setModalMapOpen } = useStore(useMapStore, (state) => ({
    setModalMapOpen: state.setModalMapOpen,
  }));
  const pathname = usePathname();
  const { isError, title, showCompare, showSpecs, getProduct } = useProducts();
  const [navBGColor, setNavBGColor] = useState<string>('');
  const urlPrefix = `/product/${slug}`;

  useEffect(() => {
    if (!slug) return;

    getProduct({
      id: slug,
    });
  }, [slug]);

  useEffect(() => {
    setNavBGColor(bgColor);
  }, [bgColor]);

  return (
    <div
      className={twMerge([
        'w-full bg-opacity-50  backdrop-blur-md',
        navBGColor,
        atTop ? 'border-transparent' : 'border-grey-900',
        'landing-header border-2 py-2',
        'rounded-3xl sm:rounded-full',
        'shadow-grey-500/50 shadow-lg',
      ])}
    >
      <div className='mx-auto w-full px-2'>
        <div
          className={`flex flex-row items-center justify-between gap-3 sm:flex-row`}
        >
          <div className='hidden pl-5 sm:block'>
            <Heading
              size={1}
              className={twMerge(
                'text-base lg:text-xl',
                themeBase === ThemeBase.light
                  ? 'text-zinc-900'
                  : 'text-zinc-300'
              )}
            >
              {title || ''}
            </Heading>
          </div>

          <div
            className={`flex grow justify-start gap-5 sm:justify-end sm:pr-5 `}
          >
            <TabItem isActive={pathname === urlPrefix} className='py-0'>
              <Link
                href={urlPrefix}
                title='Overview'
                className={`flex h-full items-center justify-center gap-2 `}
              >
                <IconComponent
                  name='MdHome'
                  fill={
                    themeBase === ThemeBase.light
                      ? 'rgb(82 82 91 / var(--tw-text-opacity, 1))'
                      : 'rgb(212 212 216 / var(--tw-text-opacity, 1))'
                  }
                  className={twMerge([
                    'size-9 md:size-4',
                    themeBase === ThemeBase.light
                      ? 'text-zinc-900'
                      : 'text-zinc-300',
                  ])}
                />
                <span
                  className={twMerge([
                    'hidden md:inline',
                    themeBase === ThemeBase.light
                      ? 'text-zinc-900'
                      : 'text-zinc-300',
                  ])}
                >
                  Overview
                </span>
              </Link>
            </TabItem>
            {showSpecs != false &&
              <TabItem
                isActive={pathname === urlPrefix + '/specification'}
                className='py-0'
              >
                <Link
                  href={urlPrefix + '/specification'}
                  title='Specs'
                  className='flex h-full items-center justify-center gap-2'
                >
                  <IconComponent
                    name='IoInformationCircleOutline'
                    fill={
                      themeBase === ThemeBase.light
                        ? 'rgb(82 82 91 / var(--tw-text-opacity, 1))'
                        : 'rgb(212 212 216 / var(--tw-text-opacity, 1))'
                    }
                    className={twMerge([
                      'size-9 md:size-4',
                      themeBase === ThemeBase.light
                        ? 'text-zinc-900'
                        : 'text-zinc-300',
                    ])}
                  />
                  <span
                    className={twMerge([
                      'hidden md:inline',
                      themeBase === ThemeBase.light
                        ? 'text-zinc-900'
                        : 'text-zinc-300',
                    ])}
                  >
                    Specs
                  </span>
                </Link>
              </TabItem>
            }
            {showCompare != false && <TabItem
              isActive={pathname === urlPrefix + '/compare'}
              className='py-0'
            >
              <Link
                href={urlPrefix + '/compare'}
                title='Compare'
                className={'flex h-full items-center justify-center gap-2'}
              >
                <IconComponent
                  name='IoGitCompareOutline'
                  fill={
                    themeBase === ThemeBase.light
                      ? 'rgb(82 82 91 / var(--tw-text-opacity, 1))'
                      : 'rgb(212 212 216 / var(--tw-text-opacity, 1))'
                  }
                  className='size-9 md:size-4'
                />
                <span
                  className={twMerge([
                    'hidden md:inline',
                    themeBase === ThemeBase.light
                      ? 'text-zinc-900'
                      : 'text-zinc-300',
                  ])}
                >
                  Compare
                </span>
              </Link>
            </TabItem>
            }
          </div>

          <div className='hidden lg:block'>
            {!isError && (
              <WhereToBuyBtn isMobile={isMobile} setMapOpen={setModalMapOpen} />
            )}
          </div >
          <div className='lg:hidden'>
            {!isError && (
              <WhereToBuyBtn
                title={BUTTON_TEXT.shopNow.btnTextMobile}
                isMobile={isMobile}
                setMapOpen={setModalMapOpen}
              />
            )}
          </div>
        </div >
      </div >
    </div >
  );
};
